'use client';

import { useState } from 'react';
import Card from '@/components/ui/Card';
import Spinner from '@/components/ui/Spinner';
import ErrorAlert from '@/components/ui/ErrorAlert';
import ESMExitedStocksFilterForm from '@/components/filters/ESMExitedStocksFilterForm';
import ESMExitedStocksTable from '@/components/esm-exited/ESMExitedStocksTable';
import { useESMExitedStocksFilterOptions } from '@/hooks/useESMExitedStocksFilterOptions';
import { useESMExitedStocks } from '@/hooks/useESMExitedStocks';
import BackButton from '@/components/ui/BackButton';
import { Search } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

export default function ESMExitedStocksPage() {
  const { filterOptions, loading: loadingFilters, error: filterError } = useESMExitedStocksFilterOptions();
  const { stocks, loading: loadingStocks, error: stocksError, fetchESMExitedStocks } = useESMExitedStocks();
  const [hasSearched, setHasSearched] = useState(false);

  const handleFilterSubmit = async (filters: Record<string, unknown>) => {
    await fetchESMExitedStocks(filters);
    setHasSearched(true);
  };

  const error = filterError || stocksError;

  if (loadingFilters) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center min-h-96">
          <Spinner size="lg" className="text-emerald-600" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10 space-y-6">
        <header className="mb-8 flex items-start gap-3">
          <div className="md:mt-0.5">
            <BackButton href="/esm-stock-analysis" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
              ESM Exited Stocks
            </h1>
            <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400">
              Stocks that have exited from the ESM framework
            </p>
          </div>
        </header>

        {error && (
          <ErrorAlert 
            message={error}
            className="mb-6"
          />
        )}

        {/* Search Form */}
        <Card className="p-0">
          <div className="flex items-center gap-2 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
            <Search className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">Filter Stocks</h2>
          </div>
          {filterOptions && (
            <ESMExitedStocksFilterForm
              filterOptions={filterOptions}
              onSubmit={handleFilterSubmit}
              isLoading={loadingStocks}
            />
          )}
        </Card>

        {/* Results */}
        {loadingStocks && (
          <Card className="p-8 text-center">
            <Spinner size="lg" className="mx-auto mb-4 text-emerald-600" />
            <p className="text-zinc-500 dark:text-zinc-400">Searching ESM exited stocks...</p>
          </Card>
        )}

        {!loadingStocks && hasSearched && stocks.length > 0 && (
          <ESMExitedStocksTable stocks={stocks} />
        )}

        {!loadingStocks && hasSearched && stocks.length === 0 && (
          <Card className="p-8 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-zinc-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-white mb-2">No stocks found</h3>
              <p className="text-zinc-500 dark:text-zinc-400 text-center max-w-md">
                Try adjusting your search criteria or check back later for updated data.
              </p>
            </div>
          </Card>
        )}

      </div>
    </PageLayout>
  );
}
